import { lazy, Suspense, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@fluentui/react-components';
import { Logo } from './components/common/Logo';
import { useAuth } from './components/auth/TeamsAuthProvider';
import { TeamsAuthProvider } from './components/auth/TeamsAuthProvider';
import { TimezoneProvider } from './components/common/TimezoneProvider';
import { Dashboard } from './components/dashboard/Dashboard';
import { MyDashboard } from './components/dashboard/MyDashboard';
import { CallList } from './components/calls/CallList';
import { InteractionDetail } from './components/calls/InteractionDetail';
import { RecordingsList } from './components/recordings/RecordingsList';
import { PlatformEmulationControl } from './components/common/PlatformEmulationControl';
import { EmptyState } from './components/common/EmptyState';
import {
  accessFallbackRoute,
  hasCompanyAccess,
  hasDashboardAccess,
  hasQueueAccess,
  homeRoute,
} from './utils/access';
import { interactionPathFor } from './utils/routes';

const CompanyPage = lazy(() =>
  import('./components/company/CompanyPage').then((m) => ({ default: m.CompanyPage })),
);
const BranchDashboard = lazy(() =>
  import('./components/branch/BranchDashboard').then((m) => ({ default: m.BranchDashboard })),
);
const QueuesPage = lazy(() =>
  import('./components/queues/QueuesPage').then((m) => ({ default: m.QueuesPage })),
);
const SettingsPage = lazy(() =>
  import('./components/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

const RouteFallback = () => {
  const { themeName } = useAuth();
  const logoTheme = themeName === 'dark' || themeName === 'contrast' ? 'dark' : 'light';
  return (
    <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}>
      <Logo variant="loading" theme={logoTheme} height={64} />
    </div>
  );
};

function RedirectPreservingSearch({ to }: { to: string }) {
  const location = useLocation();
  const destination = `${to}${location.search}`;
  return <Navigate to={destination} replace />;
}

// `/calls/:sessionId` is superseded by `/interactions/:sessionId` (Task 6):
// every list row now links straight to the new route via
// `interactionPathFor`, but old links, bookmarks and any lingering
// `/calls/:id?foo=bar` deep link must still land on the right interaction
// with its query string intact.
function CallSessionRedirect() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  return <Navigate replace to={`${interactionPathFor(sessionId ?? '')}${location.search}`} />;
}

// Every route now renders its own PageHeader (spec §4.1, Slice B step 6 +
// follow-up rollout), so the "View As" toggle is threaded to each route
// instead of a single app-level slim bar rendering it once for everyone.
interface EmulationProps {
  emulationPanelOpen: boolean;
  onToggleEmulationPanel: () => void;
}

function HomeRoute() {
  const { user } = useAuth();
  return <RedirectPreservingSearch to={homeRoute(user)} />;
}

function RecordingRoute(props: EmulationProps) {
  const { user } = useAuth();
  if (!user) return <RouteFallback />;
  return user.can_access_recordings === true
    ? <RecordingsList {...props} />
    : <Navigate to="/my" replace />;
}

function DashboardRoute(props: EmulationProps) {
  const { user } = useAuth();
  if (!user) return <RouteFallback />;
  return hasDashboardAccess(user)
    ? <Dashboard {...props} />
    : <RedirectPreservingSearch to={accessFallbackRoute(user)} />;
}

function QueuesRoute(props: EmulationProps) {
  const { user } = useAuth();
  if (!user) return <RouteFallback />;
  if (!hasQueueAccess(user)) {
    return <RedirectPreservingSearch to={accessFallbackRoute(user)} />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <QueuesPage {...props} />
    </Suspense>
  );
}

function NotFoundRoute() {
  const navigate = useNavigate();
  return (
    <div style={{ padding: 48 }}>
      <EmptyState
        title="Page not found"
        hint="The page you're looking for doesn't exist or may have moved."
        action={
          <Button appearance="primary" onClick={() => navigate('/')}>
            Go home
          </Button>
        }
      />
    </div>
  );
}

function CompanyRoute(props: EmulationProps) {
  const { user } = useAuth();
  if (!user) return <RouteFallback />;
  if (!hasCompanyAccess(user)) {
    return <RedirectPreservingSearch to={accessFallbackRoute(user)} />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <CompanyPage {...props} />
    </Suspense>
  );
}

function App() {
  // Lifted here (not local to any one page) so the "View As" trigger button,
  // now in every route's PageHeader actions slot, and the emulation panel it
  // opens, rendered by PlatformEmulationControl, share one open/closed state
  // without any page owning it.
  const [emulationPanelOpen, setEmulationPanelOpen] = useState(false);
  const emulationProps: EmulationProps = {
    emulationPanelOpen,
    onToggleEmulationPanel: () => setEmulationPanelOpen((open) => !open),
  };

  return (
    <TeamsAuthProvider>
      <TimezoneProvider>
        <BrowserRouter>
          {/* Rendered once at app level (not per-route) so the "View As"
              safety banner and Exit control are present on every route. */}
          <PlatformEmulationControl isOpen={emulationPanelOpen} onOpenChange={setEmulationPanelOpen} />
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/auth-end" element={<HomeRoute />} />
            <Route path="/my" element={<MyDashboard {...emulationProps} />} />
            <Route
              path="/branch"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <BranchDashboard {...emulationProps} />
                </Suspense>
              }
            />
            <Route path="/dashboard" element={<DashboardRoute {...emulationProps} />} />
            <Route path="/calls" element={<CallList {...emulationProps} />} />
            <Route path="/calls/:sessionId" element={<CallSessionRedirect />} />
            <Route path="/interactions/:sessionId" element={<InteractionDetail {...emulationProps} />} />
            <Route path="/recordings" element={<RecordingRoute {...emulationProps} />} />
            <Route path="/queues" element={<QueuesRoute {...emulationProps} />} />
            <Route path="/company" element={<CompanyRoute {...emulationProps} />} />
            <Route
              path="/settings"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <SettingsPage {...emulationProps} />
                </Suspense>
              }
            />
            <Route path="*" element={<NotFoundRoute />} />
          </Routes>
        </BrowserRouter>
      </TimezoneProvider>
    </TeamsAuthProvider>
  );
}

export default App;
